import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FaChevronDown } from "react-icons/fa";
import { ADMISSION_ITEMS } from "../config";
import "./AdmissionDropdown.css";

const MENU_GAP = 6;
const VIEWPORT_MARGIN = 8;

// General "Apply" CTA: same School / Sampradaya / PU College options as the header.
// The menu is portalled to <body> with fixed positioning so sections with
// overflow: hidden (CTA banners, sliders) can't clip it.
const AdmissionDropdown = ({
  label = "Apply for Admission",
  className = "btn btn-primary",
  align = "center",
}) => {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState(null);
  const [openUp, setOpenUp] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current || !menuRef.current) return;
    const btn = buttonRef.current.getBoundingClientRect();
    const menu = menuRef.current.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;

    const up =
      vh - btn.bottom < menu.height + MENU_GAP && btn.top > vh - btn.bottom;
    const top = up ? btn.top - menu.height - MENU_GAP : btn.bottom + MENU_GAP;

    let left =
      align === "left"
        ? btn.left
        : align === "right"
        ? btn.right - menu.width
        : btn.left + btn.width / 2 - menu.width / 2;
    left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(left, vw - menu.width - VIEWPORT_MARGIN)
    );

    setOpenUp(up);
    setStyle({ top, left });
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (
        !buttonRef.current?.contains(e.target) &&
        !menuRef.current?.contains(e.target)
      )
        setOpen(false);
    };
    const close = () => setOpen(false);
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const toggle = () => {
    setStyle(null);
    setOpen((prev) => !prev);
  };

  return (
    <div className="admission-dropdown">
      <button
        type="button"
        ref={buttonRef}
        className={className}
        onClick={toggle}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {label}{" "}
        <FaChevronDown
          className={`admission-dropdown-chevron ${open ? "open" : ""}`}
        />
      </button>
      {open &&
        createPortal(
          <ul
            ref={menuRef}
            className={`admission-dropdown-menu ${
              openUp ? "admission-dropdown-menu--up" : ""
            }`}
            style={style || { top: 0, left: 0, visibility: "hidden" }}
          >
            {ADMISSION_ITEMS.map((item) => (
              <li key={item.label}>
                <a
                  href={item.href}
                  className="admission-dropdown-item"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default AdmissionDropdown;
